import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Paper,
  TextField,
  Button as MuiButton,
  IconButton,
  Menu,
  MenuItem,
} from "@material-ui/core";
import {
  MoreVert,
  Close as CloseIcon,
  Edit as EditIcon,
} from "@material-ui/icons";
import { Labelbox, Button, Table } from "../../components";
import { EMPLOYEE_ACTION, PERFORMANCE_INDEX_ACTION } from "../../redux/action";
import { Toaster } from "../../utils/common";
import * as FileSaver from "file-saver";
import { utils, write } from "xlsx";
import { GetApp } from "@material-ui/icons";
import "./performanceIndex.scss";
import SearchIcon from "@material-ui/icons/Search";
import { hasHrmsAdminAccess } from "../../utils/hrmsAccess";

const PerformanceIndex = ({
  scopeEmployeeId = null,
  hideSearchControls = false,
} = {}) => {
  const dispatch = useDispatch();
  const userRole = localStorage.getItem("user_role");
  const userId = localStorage.getItem("user_id");
  const isAdmin = hasHrmsAdminAccess(userId);
  const isManager = userRole === "manager" && !isAdmin;
  const scopedEmployeeId = useMemo(
    () =>
      scopeEmployeeId === null || scopeEmployeeId === undefined
        ? ""
        : String(scopeEmployeeId).trim(),
    [scopeEmployeeId],
  );
  const isScopedEmployeeView = Boolean(scopedEmployeeId);

  const getEmployeeIdentifier = useCallback((emp) => {
    return String(
      emp?.employee_id || emp?.employeeID || emp?._id || emp?.id || "",
    ).trim();
  }, []);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [config, setConfig] = useState({
    base_bonus_percentage: 5,
    base_salary_percentage: 6,
    variable_bonus_percentage: 10,
    variable_salary_percentage: 14,
  });
  const [editingCTC, setEditingCTC] = useState({});
  const [ctcValues, setCtcValues] = useState({});
  const [performanceRatings, setPerformanceRatings] = useState({});
  // Store raw input strings for performance ratings to allow decimal entry while typing
  const [rawRatingInputs, setRawRatingInputs] = useState({});
  // Store which employee's entire row is being edited
  const [editingRow, setEditingRow] = useState(null);
  // Store original rating values before editing (for cancel functionality)
  const [originalRatings, setOriginalRatings] = useState({});
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedEmployeeForMenu, setSelectedEmployeeForMenu] = useState(null);
  // Tab state for Salary & Bonus table
  const [activeSalaryBonusTab, setActiveSalaryBonusTab] = useState("salary");
  const [isCalculationsLoading, setIsCalculationsLoading] = useState(false);
  // Search queries for each table
  const [employeeListSearch, setEmployeeListSearch] = useState("");
  const [performanceIndexSearch, setPerformanceIndexSearch] = useState("");
  const [salaryBonusSearch, setSalaryBonusSearch] = useState("");

  const {
    employee: { activeEmployees },
    performanceIndex: {
      config: configData,
      employeeCTC,
      allPerformanceRatings,
      performanceCalculations,
    },
  } = useSelector((state) => state);

  // Filter employees based on role
  const filteredEmployees = useMemo(() => {
    if (!activeEmployees || activeEmployees.length === 0) return [];
    if (isScopedEmployeeView) {
      return activeEmployees.filter(
        (emp) => getEmployeeIdentifier(emp) === scopedEmployeeId,
      );
    }
    if (isAdmin) return activeEmployees;
    if (isManager) {
      return activeEmployees.filter(
        (emp) => emp.manager_id === userId || emp.assigned_by === userId,
      );
    }
    return [];
  }, [
    activeEmployees,
    isAdmin,
    isManager,
    userId,
    getEmployeeIdentifier,
    scopedEmployeeId,
    isScopedEmployeeView,
  ]);

  // Debounce timer ref for year changes
  const yearDebounceTimerRef = useRef(null);

  // Load initial data (only on mount, not when year changes)
  useEffect(() => {
    if (isAdmin || isManager) {
      dispatch(EMPLOYEE_ACTION.getActiveEmployees());
      dispatch(PERFORMANCE_INDEX_ACTION.getPerformanceConfig());
      loadYearData();
    }
  }, [dispatch, isAdmin, isManager]);

  const loadYearData = useCallback(
    (year = selectedYear) => {
      dispatch(PERFORMANCE_INDEX_ACTION.getEmployeeCTC(year));
      dispatch(PERFORMANCE_INDEX_ACTION.getAllPerformanceRatings(year));
      dispatch(PERFORMANCE_INDEX_ACTION.getPerformanceCalculations(year));
    },
    [dispatch, selectedYear],
  );

  // Update config when loaded
  useEffect(() => {
    if (configData) {
      setConfig({
        base_bonus_percentage: configData.base_bonus_percentage || 5,
        base_salary_percentage: configData.base_salary_percentage || 6,
        variable_bonus_percentage: configData.variable_bonus_percentage || 10,
        variable_salary_percentage: configData.variable_salary_percentage || 14,
      });
    }
  }, [configData]);

  // Initialize CTC values from API
  useEffect(() => {
    if (employeeCTC && employeeCTC.length > 0) {
      const ctcMap = {};
      employeeCTC.forEach((item) => {
        ctcMap[item.employee_id] = parseFloat(item.annual_ctc) || 0;
      });
      setCtcValues((prev) => ({ ...prev, ...ctcMap }));
    }
  }, [employeeCTC]);

  // Initialize performance ratings from API (yearly ratings - single object per employee)
  useEffect(() => {
    if (allPerformanceRatings && allPerformanceRatings.length > 0) {
      const ratingsMap = {};
      allPerformanceRatings.forEach((rating) => {
        // Yearly rating - single object per employee_id
        ratingsMap[rating.employee_id] = {
          productivity: rating.productivity || 0,
          skills: rating.skills || 0,
          communication: rating.communication || 0,
          customer_support: rating.customer_support || 0,
          initiatives: rating.initiatives || 0,
          leadership: rating.leadership || 0,
          discipline: rating.discipline || 0,
          total_pi: rating.total_pi || 0,
        };
      });
      setPerformanceRatings(ratingsMap);
    } else {
      // Reset if no ratings
      setPerformanceRatings({});
    }
  }, [allPerformanceRatings]);

  // Calculate average PI for an employee (yearly rating - direct calculation)
  const calculateAveragePI = (employeeId) => {
    const rating = performanceRatings[employeeId];
    if (!rating) return 0;

    // For yearly ratings, use total_pi if available, otherwise calculate from individual metrics
    if (rating.total_pi && parseFloat(rating.total_pi) > 0) {
      return parseFloat(rating.total_pi);
    }

    // Calculate from individual metrics
    const total =
      (parseFloat(rating.productivity) || 0) +
      (parseFloat(rating.skills) || 0) +
      (parseFloat(rating.communication) || 0) +
      (parseFloat(rating.customer_support) || 0) +
      (parseFloat(rating.initiatives) || 0) +
      (parseFloat(rating.leadership) || 0) +
      (parseFloat(rating.discipline) || 0);

    return isNaN(total) ? 0 : total;
  };

  // Calculate salary and bonus for an employee
  const calculateEmployeeData = (employeeId) => {
    const ctc = parseFloat(ctcValues[employeeId] || 0) || 0;
    const avgPI = calculateAveragePI(employeeId) || 0;
    const baseSalaryPercent =
      parseFloat(config.base_salary_percentage || 0) || 0;
    const baseBonusPercent = parseFloat(config.base_bonus_percentage || 0) || 0;
    const variableSalaryPercent =
      parseFloat(config.variable_salary_percentage || 0) || 0;
    const variableBonusPercent =
      parseFloat(config.variable_bonus_percentage || 0) || 0;

    const baseSalaryHike = (ctc * baseSalaryPercent) / 100;
    const baseBonus = (ctc * baseBonusPercent) / 100;
    const variableSalaryHike =
      (avgPI / 100) * (variableSalaryPercent / 100) * ctc;
    const variableBonus = (avgPI / 100) * (variableBonusPercent / 100) * ctc;
    const totalSalaryHike = baseSalaryHike + variableSalaryHike;
    const totalBonus = baseBonus + variableBonus;
    const newCTC = ctc + totalSalaryHike;
    const halfYearBonus = totalBonus / 2;

    return {
      averagePI: avgPI || 0,
      baseSalaryHike: baseSalaryHike || 0,
      baseBonus: baseBonus || 0,
      variableSalaryHike: variableSalaryHike || 0,
      variableBonus: variableBonus || 0,
      totalSalaryHike: totalSalaryHike || 0,
      totalBonus: totalBonus || 0,
      newCTC: newCTC || 0,
      halfYearBonus: halfYearBonus || 0,
    };
  };

  // Handle config update
  const handleConfigUpdate = async () => {
    try {
      const response = await PERFORMANCE_INDEX_ACTION.updatePerformanceConfig({
        ...config,
        year: selectedYear,
      });
      if (response.status === 200 && response.data.status === 1) {
        Toaster.success("Configuration updated successfully");
        dispatch(PERFORMANCE_INDEX_ACTION.getPerformanceConfig());

        // Trigger performance calculation after config update
        try {
          setIsCalculationsLoading(true);
          const calcResponse =
            await PERFORMANCE_INDEX_ACTION.calculatePerformance(selectedYear);
          if (calcResponse.status === 200 && calcResponse.data.status === 1) {
            Toaster.success("Performance calculations updated successfully");
            // Reload data to get updated calculations from database
            await loadYearData();
          } else {
            Toaster.warning(
              "Calculations may not have been updated. Please try again.",
            );
            // Still reload data even if calculation fails
            await loadYearData();
          }
        } catch (calcError) {
          console.error("Error calculating performance:", calcError);
          Toaster.error("Error updating calculations. Please try again.");
          // Still reload data even if calculation fails
          await loadYearData();
        } finally {
          setIsCalculationsLoading(false);
        }
      } else {
        Toaster.error("Failed to update configuration");
      }
    } catch (error) {
      console.error("Error updating configuration:", error);
      Toaster.error("Error updating configuration");
    }
  };

  // Handle CTC update
  const handleCTCUpdate = async (employeeId) => {
    // Only admins can update CTC
    if (!isAdmin) {
      Toaster.error("You do not have permission to update CTC");
      return;
    }

    const ctc = parseFloat(ctcValues[employeeId] || 0);
    if (isNaN(ctc) || ctc <= 0) {
      Toaster.error("Please enter a valid CTC");
      return;
    }

    try {
      const response = await PERFORMANCE_INDEX_ACTION.updateEmployeeCTC({
        employee_id: employeeId,
        annual_ctc: ctc,
        year: selectedYear,
      });
      if (response.status === 200 && response.data.status === 1) {
        Toaster.success("CTC updated successfully");
        setEditingCTC({ ...editingCTC, [employeeId]: false });
        dispatch(PERFORMANCE_INDEX_ACTION.getEmployeeCTC(selectedYear));

        // Trigger performance calculation after CTC update
        try {
          setIsCalculationsLoading(true);
          const calcResponse =
            await PERFORMANCE_INDEX_ACTION.calculatePerformance(selectedYear);
          if (calcResponse.status === 200 && calcResponse.data.status === 1) {
            Toaster.success("Performance calculations updated successfully");
            // Reload data to get updated calculations from database
            await loadYearData();
          } else {
            Toaster.warning(
              "Calculations may not have been updated. Please try again.",
            );
            // Still reload data even if calculation fails
            await loadYearData();
          }
        } catch (calcError) {
          console.error("Error calculating performance:", calcError);
          Toaster.error("Error updating calculations. Please try again.");
          // Still reload data even if calculation fails
          await loadYearData();
        } finally {
          setIsCalculationsLoading(false);
        }
      } else {
        Toaster.error("Failed to update CTC");
      }
    } catch (error) {
      console.error("Error updating CTC:", error);
      Toaster.error("Error updating CTC");
    }
  };

  // Handle year change with debounce (2 seconds delay)
  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);

    // Clear existing timer
    if (yearDebounceTimerRef.current) {
      clearTimeout(yearDebounceTimerRef.current);
    }

    // Set new timer to call calculatePerformance and loadYearData after 2 seconds
    yearDebounceTimerRef.current = setTimeout(async () => {
      try {
        const response =
          await PERFORMANCE_INDEX_ACTION.calculatePerformance(newYear);
        if (response.status === 200) {
          Toaster.success("Performance calculations completed successfully");
          loadYearData(newYear);
        } else {
          Toaster.error("Failed to calculate performance");
        }
      } catch (error) {
        Toaster.error("Error calculating performance");
      }
    }, 2000); // 2 second delay
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (yearDebounceTimerRef.current) {
        clearTimeout(yearDebounceTimerRef.current);
      }
    };
  }, []);

  // Helper function to format numbers with period as decimal separator
  const formatNumberWithPeriod = (value, decimals = 2) => {
    const num = parseFloat(value) || 0;
    if (isNaN(num)) return "0" + "." + "0".repeat(decimals);
    return num.toFixed(decimals);
  };

  // Helper function to format number for TextField input (allows decimal entry)
  const formatNumberForInput = (value) => {
    if (value === null || value === undefined || value === "") return "";
    // Return as string, preserving the value as-is (allows partial decimal entry like "20.")
    return String(value).replace(/,/g, ".");
  };

  // Helper function to validate and normalize input value
  const handleNumberInput = (value, setValue, maxValue = null) => {
    // Remove any commas and replace with periods
    let normalized = String(value).replace(/,/g, ".");

    // Allow empty string
    if (normalized === "" || normalized === ".") {
      setValue("");
      return;
    }

    // Allow valid number patterns: digits, optional period, optional digits
    // Examples: "20", "20.", "20.5", "20.50"
    const validPattern = /^-?\d*\.?\d*$/;

    if (validPattern.test(normalized)) {
      // If it ends with just a period, allow it (user might be typing "20.")
      if (normalized.endsWith(".")) {
        setValue(normalized);
        return;
      }

      const num = parseFloat(normalized);
      if (!isNaN(num)) {
        // Check max value if provided
        if (maxValue !== null && num > maxValue) {
          setValue(maxValue.toString());
          return;
        }
        // Allow negative only if explicitly typed (for now, we'll prevent negatives)
        if (num < 0) {
          setValue("0");
          return;
        }
        setValue(normalized);
      } else {
        // Invalid input, keep previous value or set to empty
        setValue("");
      }
    }
    // If pattern doesn't match, don't update (prevents invalid characters)
  };

  // Helper function to parse number from TextField input (handles both comma and period)
  const parseNumberFromInput = (value) => {
    if (!value || value === "" || value === ".") return 0;
    // Replace comma with period if present
    const normalizedValue = String(value).replace(/,/g, ".");
    const num = parseFloat(normalizedValue);
    return isNaN(num) ? 0 : num;
  };

  // Get metric values for an employee (yearly rating - direct values)
  const getMetricValues = (employeeId) => {
    const rating = performanceRatings[employeeId];
    if (!rating) {
      return {
        productivity: 0,
        skills: 0,
        communication: 0,
        customer_support: 0,
        initiatives: 0,
        leadership: 0,
        discipline: 0,
        total_pi: 0,
      };
    }

    // Calculate total_pi from individual metrics if not provided
    const totalPI =
      rating.total_pi ||
      (parseFloat(rating.productivity) || 0) +
        (parseFloat(rating.skills) || 0) +
        (parseFloat(rating.communication) || 0) +
        (parseFloat(rating.customer_support) || 0) +
        (parseFloat(rating.initiatives) || 0) +
        (parseFloat(rating.leadership) || 0) +
        (parseFloat(rating.discipline) || 0);

    return {
      productivity: parseFloat(rating.productivity) || 0,
      skills: parseFloat(rating.skills) || 0,
      communication: parseFloat(rating.communication) || 0,
      customer_support: parseFloat(rating.customer_support) || 0,
      initiatives: parseFloat(rating.initiatives) || 0,
      leadership: parseFloat(rating.leadership) || 0,
      discipline: parseFloat(rating.discipline) || 0,
      total_pi: parseFloat(totalPI) || 0,
    };
  };

  // Prepare base employee list data (pure values) with search filter
  const employeeListData = useMemo(() => {
    const search = employeeListSearch.trim().toLowerCase();
    return filteredEmployees
      .filter((emp) => {
        if (!search) return true;
        const name = `${emp.first_name} ${emp.last_name}`.toLowerCase();
        return name.includes(search);
      })
      .map((emp) => {
        const calcData = calculateEmployeeData(emp.employee_id);
        // For yearly ratings, check if rating exists (has any value > 0)
        const hasRating =
          performanceRatings[emp.employee_id] &&
          Object.values(performanceRatings[emp.employee_id]).some(
            (v) => parseFloat(v) > 0,
          );

        return {
          employee_id: emp.employee_id,
          employee_name: `${emp.first_name} ${emp.last_name}`,
          ctc: ctcValues[emp.employee_id] || 0,
          average_pi: calcData.averagePI || 0,
          has_rating: hasRating || false,
        };
      });
  }, [filteredEmployees, ctcValues, performanceRatings, employeeListSearch]);

  // Prepare table rows for Employee List (includes JSX for cells)
  // Use a stable key based on employee IDs to prevent pagination reset
  const employeeTableRowsKey = useMemo(() => {
    return employeeListData.map((emp) => emp.employee_id).join(",");
  }, [employeeListData]);

  const employeeTableRows = useMemo(() => {
    return employeeListData.map((emp, index) => {
      return {
        si_no: index + 1,
        employee_name: emp.employee_name,
        annual_ctc:
          isAdmin && editingCTC[emp.employee_id] ? (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <TextField
                type="text"
                value={
                  rawRatingInputs[`ctc_${emp.employee_id}`] !== undefined
                    ? rawRatingInputs[`ctc_${emp.employee_id}`]
                    : formatNumberForInput(ctcValues[emp.employee_id])
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const normalized = inputValue.replace(/,/g, ".");
                  if (normalized === "" || /^-?\d*\.?\d*$/.test(normalized)) {
                    setRawRatingInputs({
                      ...rawRatingInputs,
                      [`ctc_${emp.employee_id}`]: normalized,
                    });
                    if (normalized !== "" && normalized !== ".") {
                      const num = parseFloat(normalized);
                      if (!isNaN(num)) {
                        setCtcValues({
                          ...ctcValues,
                          [emp.employee_id]: Math.max(0, num),
                        });
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.replace(/,/g, ".");
                  const num =
                    inputValue === "" || inputValue === "."
                      ? 0
                      : parseFloat(inputValue);
                  const finalValue = isNaN(num) ? 0 : Math.max(0, num);
                  const newRawInputs = { ...rawRatingInputs };
                  delete newRawInputs[`ctc_${emp.employee_id}`];
                  setRawRatingInputs(newRawInputs);
                  setCtcValues({
                    ...ctcValues,
                    [emp.employee_id]: finalValue,
                  });
                }}
                size="small"
                style={{ width: "120px" }}
              />
              <MuiButton
                size="small"
                variant="contained"
                onClick={() => handleCTCUpdate(emp.employee_id)}
                style={{
                  backgroundColor: "#1C74BC",
                  borderColor: "#1C74BC",
                  color: "white",
                }}
              >
                Save
              </MuiButton>
              <MuiButton
                size="small"
                variant="outlined"
                onClick={() => {
                  setEditingCTC({ ...editingCTC, [emp.employee_id]: false });
                  const originalCTC = employeeCTC.find(
                    (e) => e.employee_id === emp.employee_id,
                  );
                  if (originalCTC) {
                    setCtcValues({
                      ...ctcValues,
                      [emp.employee_id]: originalCTC.annual_ctc || 0,
                    });
                  }
                }}
              >
                Cancel
              </MuiButton>
            </div>
          ) : (
            <span>
              {parseFloat(ctcValues[emp.employee_id] || 0).toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}
            </span>
          ),
        employee_pi: formatNumberWithPeriod(emp.average_pi || 0, 2),
        action: isAdmin ? (
          <IconButton
            size="small"
            onClick={(e) => {
              // Only open menu; do not change data so pagination stays on current page
              setMenuAnchor(e.currentTarget);
              setSelectedEmployeeForMenu(emp.employee_id);
            }}
          >
            <MoreVert />
          </IconButton>
        ) : null,
        employee_id: emp.employee_id,
      };
    });
  }, [
    employeeListData,
    isAdmin,
    editingCTC,
    rawRatingInputs,
    ctcValues,
    employeeCTC,
  ]);

  // Handle save entire row of ratings
  const handleSaveRowRatings = useCallback(
    async (employeeId) => {
      const currentRating = performanceRatings[employeeId] || {};

      // Calculate total_pi from all fields
      const totalPI =
        (parseFloat(currentRating.productivity) || 0) +
        (parseFloat(currentRating.skills) || 0) +
        (parseFloat(currentRating.communication) || 0) +
        (parseFloat(currentRating.customer_support) || 0) +
        (parseFloat(currentRating.initiatives) || 0) +
        (parseFloat(currentRating.leadership) || 0) +
        (parseFloat(currentRating.discipline) || 0);

      try {
        // Save to API - new yearly format (direct fields, no month, no ratings array)
        const response = await PERFORMANCE_INDEX_ACTION.savePerformanceRating({
          employee_id: employeeId,
          year: selectedYear,
          productivity: parseFloat(currentRating.productivity) || 0,
          skills: parseFloat(currentRating.skills) || 0,
          communication: parseFloat(currentRating.communication) || 0,
          customer_support: parseFloat(currentRating.customer_support) || 0,
          initiatives: parseFloat(currentRating.initiatives) || 0,
          leadership: parseFloat(currentRating.leadership) || 0,
          discipline: parseFloat(currentRating.discipline) || 0,
        });

        if (response.status === 200 && response.data.status === 1) {
          Toaster.success("Ratings updated successfully");
          // Update total_pi in local state
          setPerformanceRatings((prev) => ({
            ...prev,
            [employeeId]: {
              ...prev[employeeId],
              total_pi: totalPI,
            },
          }));
          // Close edit mode
          setEditingRow(null);
          // Clear original ratings
          const newOriginalRatings = { ...originalRatings };
          delete newOriginalRatings[employeeId];
          setOriginalRatings(newOriginalRatings);
          // Clear raw inputs for this employee
          const newRawInputs = { ...rawRatingInputs };
          Object.keys(newRawInputs).forEach((key) => {
            if (key.startsWith(`${employeeId}_`)) {
              delete newRawInputs[key];
            }
          });
          setRawRatingInputs(newRawInputs);

          // Trigger performance calculation on backend to update calculations in database
          try {
            const calcResponse =
              await PERFORMANCE_INDEX_ACTION.calculatePerformance(selectedYear);
            if (calcResponse.status === 200 && calcResponse.data.status === 1) {
              Toaster.success("Performance calculations updated successfully");
              // Reload data to get updated calculations from database
              await loadYearData();
            } else {
              Toaster.warning(
                "Calculations may not have been saved. Please try again.",
              );
              // Still reload data even if calculation fails
              await loadYearData();
            }
          } catch (calcError) {
            console.error("Error calculating performance:", calcError);
            Toaster.error("Error updating calculations. Please try again.");
            // Still reload data even if calculation fails
            await loadYearData();
          }
        } else {
          Toaster.error("Failed to update ratings");
        }
      } catch (error) {
        console.error("Error updating ratings:", error);
        Toaster.error("Error updating ratings");
      }
    },
    [
      selectedYear,
      performanceRatings,
      rawRatingInputs,
      originalRatings,
      loadYearData,
    ],
  );

  // Handle cancel edit
  const handleCancelEdit = useCallback(
    (employeeId) => {
      setEditingRow(null);
      // Restore original ratings
      if (originalRatings[employeeId]) {
        setPerformanceRatings((prev) => ({
          ...prev,
          [employeeId]: { ...originalRatings[employeeId] },
        }));
        // Remove from originalRatings
        const newOriginalRatings = { ...originalRatings };
        delete newOriginalRatings[employeeId];
        setOriginalRatings(newOriginalRatings);
      }
      // Clear raw inputs for this employee
      const newRawInputs = { ...rawRatingInputs };
      Object.keys(newRawInputs).forEach((key) => {
        if (key.startsWith(`${employeeId}_`)) {
          delete newRawInputs[key];
        }
      });
      setRawRatingInputs(newRawInputs);
    },
    [rawRatingInputs, originalRatings],
  );

  // Export Employee List to Excel
  const handleExportEmployeeList = () => {
    const exportData = employeeListData.map((emp, index) => ({
      "SI No": index + 1,
      "Employee Name": emp.employee_name,
      "Annual CTC": isAdmin ? formatNumberWithPeriod(emp.ctc, 2) : "",
      "Employee PI": formatNumberWithPeriod(emp.average_pi, 2),
    }));

    const headers = isAdmin
      ? ["SI No", "Employee Name", "Annual CTC", "Employee PI"]
      : ["SI No", "Employee Name", "Employee PI"];

    const ws = utils.aoa_to_sheet([headers]);
    // Style headers
    headers.forEach((_, index) => {
      const cellAddress = utils.encode_cell({ r: 0, c: index });
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: "E6F0FA" } },
          font: { bold: true },
        };
      }
    });

    utils.sheet_add_json(ws, exportData, {
      origin: "A2",
      skipHeader: true,
    });

    const wb = {
      Sheets: { "Employee List": ws },
      SheetNames: ["Employee List"],
    };

    const excelBuffer = write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Employee_List_${selectedYear}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    Toaster.success("Employee List exported successfully");
  };

  // Export Performance Index to Excel
  const handleExportPerformanceIndex = () => {
    // Apply search filter to match what's displayed in the table
    const search = performanceIndexSearch.trim().toLowerCase();
    const filteredData = filteredEmployees.filter((emp) => {
      if (!search) return true;
      const name = `${emp.first_name} ${emp.last_name}`.toLowerCase();
      return name.includes(search);
    });

    const exportData = filteredData.map((emp, index) => {
      const metrics = getMetricValues(emp.employee_id);
      return {
        "SI No": index + 1,
        "Employee Name": `${emp.first_name} ${emp.last_name}`,
        "Productivity (25)": formatNumberWithPeriod(metrics.productivity, 2),
        "Technical Skills (15)": formatNumberWithPeriod(metrics.skills, 2),
        "Communication (10)": formatNumberWithPeriod(metrics.communication, 2),
        "Quality/ Customer Satisfaction (15)": formatNumberWithPeriod(
          metrics.customer_support,
          2,
        ),
        "Leadership/Teamwork (10)": formatNumberWithPeriod(
          metrics.initiatives,
          2,
        ),
        "Peer Review (10)": formatNumberWithPeriod(metrics.leadership, 2),
        "HR Feedback (15)": formatNumberWithPeriod(metrics.discipline, 2),
        "Total PI": formatNumberWithPeriod(metrics.total_pi, 2),
      };
    });

    const headers = [
      "SI No",
      "Employee Name",
      "Productivity (25)",
      "Technical Skills (15)",
      "Communication (10)",
      "Quality/ Customer Satisfaction (15)",
      "Leadership/Teamwork (10)",
      "Peer Review (10)",
      "HR Feedback (15)",
      "Total PI",
    ];

    const ws = utils.aoa_to_sheet([headers]);
    // Style headers
    headers.forEach((_, index) => {
      const cellAddress = utils.encode_cell({ r: 0, c: index });
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: "E6F0FA" } },
          font: { bold: true },
        };
      }
    });

    utils.sheet_add_json(ws, exportData, {
      origin: "A2",
      skipHeader: true,
    });

    const wb = {
      Sheets: { "Performance Index": ws },
      SheetNames: ["Performance Index"],
    };

    const excelBuffer = write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Performance_Index_${selectedYear}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    Toaster.success("Performance Index exported successfully");
  };

  // Export Salary & Bonus Calculations to Excel (with two sheets)
  const handleExportSalaryBonus = () => {
    // Apply search filter to match what's displayed in the table
    const search = salaryBonusSearch.trim().toLowerCase();
    const filteredData = filteredEmployees.filter((emp) => {
      if (!search) return true;
      const name = `${emp.first_name} ${emp.last_name}`.toLowerCase();
      return name.includes(search);
    });

    // Prepare Salary data
    const salaryData = filteredData.map((emp, index) => {
      const apiCalculation = performanceCalculations?.find(
        (calc) => calc.employee_id === emp.employee_id,
      );
      const calcData = apiCalculation
        ? {
            baseSalaryHike: parseFloat(apiCalculation.base_salary_hike) || 0,
            variableSalaryHike:
              parseFloat(apiCalculation.variable_salary_hike) || 0,
            totalSalaryHike: parseFloat(apiCalculation.total_salary_hike) || 0,
            newCTC: parseFloat(apiCalculation.new_ctc) || 0,
          }
        : calculateEmployeeData(emp.employee_id);

      const ctcValue = parseFloat(ctcValues[emp.employee_id] || 0) || 0;
      const formattedCTC = ctcValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const baseSalaryHikeLabel = `Base Salary Hike (${formatNumberWithPeriod(
        config.base_salary_percentage,
        2,
      )}%)`;
      const variableSalaryHikeLabel = `Variable Salary Hike (${formatNumberWithPeriod(
        config.variable_salary_percentage,
        2,
      )}%)`;

      return {
        "SI No": index + 1,
        "Employee Name": `${emp.first_name} ${emp.last_name}`,
        "Annual CTC": formattedCTC,
        [baseSalaryHikeLabel]: formatNumberWithPeriod(
          calcData.baseSalaryHike,
          2,
        ),
        [variableSalaryHikeLabel]: formatNumberWithPeriod(
          calcData.variableSalaryHike,
          2,
        ),
        "Total Salary Hike": formatNumberWithPeriod(
          calcData.totalSalaryHike,
          2,
        ),
        "New CTC": formatNumberWithPeriod(calcData.newCTC, 2),
      };
    });

    // Prepare Bonus data
    const bonusData = filteredData.map((emp, index) => {
      const apiCalculation = performanceCalculations?.find(
        (calc) => calc.employee_id === emp.employee_id,
      );
      const calcData = apiCalculation
        ? {
            baseBonus: parseFloat(apiCalculation.base_bonus) || 0,
            variableBonus: parseFloat(apiCalculation.variable_bonus) || 0,
            totalBonus: parseFloat(apiCalculation.total_bonus) || 0,
            halfYearBonus: parseFloat(apiCalculation.half_year_bonus) || 0,
          }
        : calculateEmployeeData(emp.employee_id);

      const ctcValue = parseFloat(ctcValues[emp.employee_id] || 0) || 0;
      const formattedCTC = ctcValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const baseBonusLabel = `Base Bonus (${formatNumberWithPeriod(
        config.base_bonus_percentage,
        2,
      )}%)`;
      const variableBonusLabel = `Variable Bonus (${formatNumberWithPeriod(
        config.variable_bonus_percentage,
        2,
      )}%)`;

      return {
        "SI No": index + 1,
        "Employee Name": `${emp.first_name} ${emp.last_name}`,
        "Annual CTC": formattedCTC,
        [baseBonusLabel]: formatNumberWithPeriod(calcData.baseBonus, 2),
        [variableBonusLabel]: formatNumberWithPeriod(calcData.variableBonus, 2),
        "Total Bonus": formatNumberWithPeriod(calcData.totalBonus, 2),
        "Half Year Bonus": formatNumberWithPeriod(calcData.halfYearBonus, 2),
      };
    });

    // Create Salary sheet
    const salaryHeaders = [
      "SI No",
      "Employee Name",
      "Annual CTC",
      `Base Salary Hike (${formatNumberWithPeriod(
        config.base_salary_percentage,
        2,
      )}%)`,
      `Variable Salary Hike (${formatNumberWithPeriod(
        config.variable_salary_percentage,
        2,
      )}%)`,
      "Total Salary Hike",
      "New CTC",
    ];

    const salaryWs = utils.aoa_to_sheet([salaryHeaders]);
    // Style headers
    salaryHeaders.forEach((_, index) => {
      const cellAddress = utils.encode_cell({ r: 0, c: index });
      if (salaryWs[cellAddress]) {
        salaryWs[cellAddress].s = {
          fill: { fgColor: { rgb: "E6F0FA" } },
          font: { bold: true },
        };
      }
    });

    utils.sheet_add_json(salaryWs, salaryData, {
      origin: "A2",
      skipHeader: true,
    });

    // Create Bonus sheet
    const bonusHeaders = [
      "SI No",
      "Employee Name",
      "Annual CTC",
      `Base Bonus (${formatNumberWithPeriod(config.base_bonus_percentage, 2)}%)`,
      `Variable Bonus (${formatNumberWithPeriod(
        config.variable_bonus_percentage,
        2,
      )}%)`,
      "Total Bonus",
      "Half Year Bonus",
    ];

    const bonusWs = utils.aoa_to_sheet([bonusHeaders]);
    // Style headers
    bonusHeaders.forEach((_, index) => {
      const cellAddress = utils.encode_cell({ r: 0, c: index });
      if (bonusWs[cellAddress]) {
        bonusWs[cellAddress].s = {
          fill: { fgColor: { rgb: "E6F0FA" } },
          font: { bold: true },
        };
      }
    });

    utils.sheet_add_json(bonusWs, bonusData, {
      origin: "A2",
      skipHeader: true,
    });

    // Create workbook with both sheets
    const wb = {
      Sheets: { Salary: salaryWs, Bonus: bonusWs },
      SheetNames: ["Salary", "Bonus"],
    };

    const excelBuffer = write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Salary_Bonus_Calculations_${selectedYear}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    Toaster.success("Salary & Bonus Calculations exported successfully");
  };

  // Prepare Performance Index summary table data with row-level editing and search filter
  const performanceIndexData = useMemo(() => {
    const search = performanceIndexSearch.trim().toLowerCase();
    return filteredEmployees
      .filter((emp) => {
        if (!search) return true;
        const name = `${emp.first_name} ${emp.last_name}`.toLowerCase();
        return name.includes(search);
      })
      .map((emp, index) => {
        const metrics = getMetricValues(emp.employee_id);
        const isRowEditing = editingRow === emp.employee_id;

        // Helper to render editable field
        const renderEditableField = (fieldName, value) => {
          const fieldKey = `${emp.employee_id}_${fieldName}`;

          if (isRowEditing && isAdmin) {
            return (
              <TextField
                type="text"
                value={
                  rawRatingInputs[fieldKey] !== undefined
                    ? rawRatingInputs[fieldKey]
                    : formatNumberForInput(value)
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const normalized = inputValue.replace(/,/g, ".");
                  if (normalized === "" || /^-?\d*\.?\d*$/.test(normalized)) {
                    setRawRatingInputs({
                      ...rawRatingInputs,
                      [fieldKey]: normalized,
                    });
                    if (normalized !== "" && normalized !== ".") {
                      const num = parseFloat(normalized);
                      if (!isNaN(num)) {
                        setPerformanceRatings((prev) => ({
                          ...prev,
                          [emp.employee_id]: {
                            ...(prev[emp.employee_id] || {}),
                            [fieldName]: Math.max(0, num),
                          },
                        }));
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.replace(/,/g, ".");
                  const num =
                    inputValue === "" || inputValue === "."
                      ? 0
                      : parseFloat(inputValue);
                  const finalValue = isNaN(num) ? 0 : Math.max(0, num);
                  const newRawInputs = { ...rawRatingInputs };
                  delete newRawInputs[fieldKey];
                  setRawRatingInputs(newRawInputs);
                  setPerformanceRatings((prev) => ({
                    ...prev,
                    [emp.employee_id]: {
                      ...(prev[emp.employee_id] || {}),
                      [fieldName]: finalValue,
                    },
                  }));
                }}
                size="small"
                style={{ width: "80px" }}
              />
            );
          }

          return formatNumberWithPeriod(value, 2);
        };

        // Calculate total_pi for display (recalculate from current values)
        const currentRating = performanceRatings[emp.employee_id] || {};
        const displayTotalPI = isRowEditing
          ? (parseFloat(currentRating.productivity) || 0) +
            (parseFloat(currentRating.skills) || 0) +
            (parseFloat(currentRating.communication) || 0) +
            (parseFloat(currentRating.customer_support) || 0) +
            (parseFloat(currentRating.initiatives) || 0) +
            (parseFloat(currentRating.leadership) || 0) +
            (parseFloat(currentRating.discipline) || 0)
          : metrics.total_pi;

        return {
          si_no: index + 1,
          employee_name: `${emp.first_name} ${emp.last_name}`,
          productivity: renderEditableField(
            "productivity",
            metrics.productivity,
          ),
          skills: renderEditableField("skills", metrics.skills),
          communication: renderEditableField(
            "communication",
            metrics.communication,
          ),
          customer_support: renderEditableField(
            "customer_support",
            metrics.customer_support,
          ),
          initiatives: renderEditableField("initiatives", metrics.initiatives),
          leadership: renderEditableField("leadership", metrics.leadership),
          discipline: renderEditableField("discipline", metrics.discipline),
          total_pi: formatNumberWithPeriod(displayTotalPI, 2),
          action: isAdmin ? (
            isRowEditing ? (
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <MuiButton
                  size="small"
                  variant="contained"
                  onClick={() => handleSaveRowRatings(emp.employee_id)}
                  style={{
                    backgroundColor: "#1C74BC",
                    borderColor: "#1C74BC",
                    color: "white",
                  }}
                >
                  Save
                </MuiButton>
                <MuiButton
                  size="small"
                  variant="outlined"
                  onClick={() => handleCancelEdit(emp.employee_id)}
                >
                  Cancel
                </MuiButton>
              </div>
            ) : (
              <IconButton
                size="small"
                onClick={() => {
                  // Store original values before editing
                  const currentRating =
                    performanceRatings[emp.employee_id] || {};
                  setOriginalRatings((prev) => ({
                    ...prev,
                    [emp.employee_id]: { ...currentRating },
                  }));
                  setEditingRow(emp.employee_id);
                }}
              >
                <EditIcon style={{ color: "#1C74BC" }} />
              </IconButton>
            )
          ) : null,
          employee_id: emp.employee_id,
        };
      });
  }, [
    filteredEmployees,
    performanceRatings,
    editingRow,
    rawRatingInputs,
    isAdmin,
    selectedYear,
    handleSaveRowRatings,
    handleCancelEdit,
    performanceIndexSearch,
  ]);

  return (
    <div className="performance-index-page">
      <div className="performance-index-header">
        <h1 className="page-title"></h1>
        <div className="header-actions">
          <TextField
            label="Year"
            type="text"
            value={selectedYear}
            onChange={(e) => {
              const value =
                parseInt(e.target.value) || new Date().getFullYear();
              handleYearChange(value);
            }}
            variant="outlined"
            size="small"
            style={{ width: "120px" }}
          />
        </div>
      </div>

      {/* Admin Configuration Section */}
      {isAdmin && !isScopedEmployeeView && (
        <Paper className="config-section">
          <h2 className="section-title">Configuration</h2>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Base Bonus %"
                type="text"
                value={
                  rawRatingInputs["config_base_bonus"] !== undefined
                    ? rawRatingInputs["config_base_bonus"]
                    : formatNumberForInput(config.base_bonus_percentage)
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const normalized = inputValue.replace(/,/g, ".");
                  if (normalized === "" || /^-?\d*\.?\d*$/.test(normalized)) {
                    setRawRatingInputs({
                      ...rawRatingInputs,
                      config_base_bonus: normalized,
                    });
                    if (normalized !== "" && normalized !== ".") {
                      const num = parseFloat(normalized);
                      if (!isNaN(num)) {
                        setConfig({
                          ...config,
                          base_bonus_percentage: Math.max(0, num),
                        });
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.replace(/,/g, ".");
                  const num =
                    inputValue === "" || inputValue === "."
                      ? 0
                      : parseFloat(inputValue);
                  const finalValue = isNaN(num) ? 0 : Math.max(0, num);
                  const newRawInputs = { ...rawRatingInputs };
                  delete newRawInputs["config_base_bonus"];
                  setRawRatingInputs(newRawInputs);
                  setConfig({ ...config, base_bonus_percentage: finalValue });
                }}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Base Salary %"
                type="text"
                value={
                  rawRatingInputs["config_base_salary"] !== undefined
                    ? rawRatingInputs["config_base_salary"]
                    : formatNumberForInput(config.base_salary_percentage)
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const normalized = inputValue.replace(/,/g, ".");
                  if (normalized === "" || /^-?\d*\.?\d*$/.test(normalized)) {
                    setRawRatingInputs({
                      ...rawRatingInputs,
                      config_base_salary: normalized,
                    });
                    if (normalized !== "" && normalized !== ".") {
                      const num = parseFloat(normalized);
                      if (!isNaN(num)) {
                        setConfig({
                          ...config,
                          base_salary_percentage: Math.max(0, num),
                        });
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.replace(/,/g, ".");
                  const num =
                    inputValue === "" || inputValue === "."
                      ? 0
                      : parseFloat(inputValue);
                  const finalValue = isNaN(num) ? 0 : Math.max(0, num);
                  const newRawInputs = { ...rawRatingInputs };
                  delete newRawInputs["config_base_salary"];
                  setRawRatingInputs(newRawInputs);
                  setConfig({ ...config, base_salary_percentage: finalValue });
                }}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Variable Bonus %"
                type="text"
                value={
                  rawRatingInputs["config_variable_bonus"] !== undefined
                    ? rawRatingInputs["config_variable_bonus"]
                    : formatNumberForInput(config.variable_bonus_percentage)
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const normalized = inputValue.replace(/,/g, ".");
                  if (normalized === "" || /^-?\d*\.?\d*$/.test(normalized)) {
                    setRawRatingInputs({
                      ...rawRatingInputs,
                      config_variable_bonus: normalized,
                    });
                    if (normalized !== "" && normalized !== ".") {
                      const num = parseFloat(normalized);
                      if (!isNaN(num)) {
                        setConfig({
                          ...config,
                          variable_bonus_percentage: Math.max(0, num),
                        });
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.replace(/,/g, ".");
                  const num =
                    inputValue === "" || inputValue === "."
                      ? 0
                      : parseFloat(inputValue);
                  const finalValue = isNaN(num) ? 0 : Math.max(0, num);
                  const newRawInputs = { ...rawRatingInputs };
                  delete newRawInputs["config_variable_bonus"];
                  setRawRatingInputs(newRawInputs);
                  setConfig({
                    ...config,
                    variable_bonus_percentage: finalValue,
                  });
                }}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Variable Salary %"
                type="text"
                value={
                  rawRatingInputs["config_variable_salary"] !== undefined
                    ? rawRatingInputs["config_variable_salary"]
                    : formatNumberForInput(config.variable_salary_percentage)
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const normalized = inputValue.replace(/,/g, ".");
                  if (normalized === "" || /^-?\d*\.?\d*$/.test(normalized)) {
                    setRawRatingInputs({
                      ...rawRatingInputs,
                      config_variable_salary: normalized,
                    });
                    if (normalized !== "" && normalized !== ".") {
                      const num = parseFloat(normalized);
                      if (!isNaN(num)) {
                        setConfig({
                          ...config,
                          variable_salary_percentage: Math.max(0, num),
                        });
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.replace(/,/g, ".");
                  const num =
                    inputValue === "" || inputValue === "."
                      ? 0
                      : parseFloat(inputValue);
                  const finalValue = isNaN(num) ? 0 : Math.max(0, num);
                  const newRawInputs = { ...rawRatingInputs };
                  delete newRawInputs["config_variable_salary"];
                  setRawRatingInputs(newRawInputs);
                  setConfig({
                    ...config,
                    variable_salary_percentage: finalValue,
                  });
                }}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                text="Update Configuration"
                handleClick={handleConfigUpdate}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Employee List Table */}
      <Paper className="employee-table-panel panel">
        <div className="panel-heading">
          <h3 className="panel-title">Employee List</h3>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "24px",
          }}
        >
          {!isScopedEmployeeView && !hideSearchControls ? (
            <div className="dash-search" style={{ flex: 1, maxWidth: "300px" }}>
              <SearchIcon className="dash-search-icon" />
              <input
                type="text"
                placeholder="Search Employee"
                aria-label="Search Employee"
                value={employeeListSearch}
                onChange={(e) => setEmployeeListSearch(e.target.value)}
              />
            </div>
          ) : (
            <div style={{ flex: 1 }} />
          )}
          {/* <MuiButton
            variant="outlined"
            size="small"
            startIcon={<GetApp />}
            onClick={handleExportEmployeeList}
            style={{ color: "#1C74BC", borderColor: "#1C74BC" }}
          >
            Export to Excel
          </MuiButton> */}
        </div>
        <Table
          title=""
          aligncss="dashboard-table"
          header={
            isAdmin
              ? [
                  { id: "1", label: "SI No" },
                  { id: "2", label: "Employee Name" },
                  { id: "3", label: "Annual CTC" },
                  { id: "4", label: "Employee PI" },
                  { id: "5", label: "Action" },
                ]
              : [
                  { id: "1", label: "SI No" },
                  { id: "2", label: "Employee Name" },
                  { id: "3", label: "Employee PI" },
                ]
          }
          data={employeeTableRows}
          hiddenFields={
            isAdmin ? ["employee_id"] : ["employee_id", "annual_ctc", "action"]
          }
        />
      </Paper>

      {/* Performance Index Summary Table */}
      <Paper className="employee-table-panel panel" style={{ marginTop: 25 }}>
        <div className="panel-heading">
          <h3 className="panel-title">Performance Index</h3>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "24px",
          }}
        >
          {!isScopedEmployeeView && !hideSearchControls ? (
            <div className="dash-search" style={{ flex: 1, maxWidth: "300px" }}>
              <SearchIcon className="dash-search-icon" />
              <input
                type="text"
                placeholder="Search Employee"
                aria-label="Search Employee"
                value={performanceIndexSearch}
                onChange={(e) => setPerformanceIndexSearch(e.target.value)}
              />
            </div>
          ) : (
            <div style={{ flex: 1 }} />
          )}
          {/* <MuiButton
            variant="outlined"
            size="small"
            startIcon={<GetApp />}
            onClick={handleExportPerformanceIndex}
            style={{ color: "#1C74BC", borderColor: "#1C74BC" }}
          >
            Export to Excel
          </MuiButton> */}
        </div>
        <Table
          title=""
          aligncss="dashboard-table"
          header={
            isAdmin
              ? [
                  { id: "1", label: "SI No" },
                  { id: "2", label: "Employee Name" },
                  { id: "3", label: "Productivity (25)" },
                  { id: "4", label: "Technical Skills (15)" },
                  { id: "5", label: "Communication (10)" },
                  { id: "6", label: "Quality/ Customer Satisfaction  (15)" },
                  { id: "7", label: "Leadership/Teamwork (10)" },
                  { id: "8", label: "Peer Rivew (10)" },
                  { id: "9", label: "HR Feedback (15)" },
                  { id: "10", label: "Total PI" },
                  { id: "11", label: "Action" },
                ]
              : [
                  { id: "1", label: "SI No" },
                  { id: "2", label: "Employee Name" },
                  { id: "3", label: "Productivity (25)" },
                  { id: "4", label: "Technical Skills (15)" },
                  { id: "5", label: "Communication (10)" },
                  { id: "6", label: "Quality/ Customer Satisfaction  (15)" },
                  { id: "7", label: "Leadership/Teamwork (10)" },
                  { id: "8", label: "Peer Rivew (10)" },
                  { id: "9", label: "HR Feedback (15)" },
                  { id: "10", label: "Total PI" },
                ]
          }
          data={performanceIndexData}
          hiddenFields={isAdmin ? ["employee_id"] : ["employee_id", "action"]}
        />
      </Paper>

      {/* Menu for Actions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setSelectedEmployeeForMenu(null);
        }}
      >
        {isAdmin && (
          <MenuItem
            onClick={() => {
              if (selectedEmployeeForMenu) {
                setEditingCTC({
                  ...editingCTC,
                  [selectedEmployeeForMenu]: true,
                });
              }
              setMenuAnchor(null);
              setSelectedEmployeeForMenu(null);
            }}
          >
            Edit CTC
          </MenuItem>
        )}
      </Menu>

      {/* Salary and Bonus Calculations Table - Admin only */}
      {isAdmin && (
        <Paper className="employee-table-panel panel" style={{ marginTop: 25 }}>
          <div className="panel-heading" style={{ marginBottom: 10 }}>
            <h3 className="panel-title">Salary & Bonus Calculations</h3>
          </div>

          {/* Loading Indicator */}
          {isCalculationsLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px 20px",
                minHeight: "200px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    margin: "0 auto 15px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #1C74BC",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                <p style={{ color: "#666", fontSize: "14px" }}>
                  Updating calculations...
                </p>
              </div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {!isCalculationsLoading && (
            <>
              {/* Tabs */}
              <div className="salary-bonus-tabs">
                <button
                  className={`salary-bonus-tab ${
                    activeSalaryBonusTab === "salary" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveSalaryBonusTab("salary")}
                >
                  Salary
                </button>
                <button
                  className={`salary-bonus-tab ${
                    activeSalaryBonusTab === "bonus" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveSalaryBonusTab("bonus")}
                >
                  Bonus
                </button>
              </div>

              {/* Search and Export below tabs */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "24px",
                }}
              >
                {!isScopedEmployeeView && !hideSearchControls ? (
                  <div
                    className="dash-search"
                    style={{ flex: 1, maxWidth: "300px" }}
                  >
                    <SearchIcon className="dash-search-icon" />
                    <input
                      type="text"
                      placeholder="Search Employee"
                      aria-label="Search Employee"
                      value={salaryBonusSearch}
                      onChange={(e) => setSalaryBonusSearch(e.target.value)}
                    />
                  </div>
                ) : (
                  <div style={{ flex: 1 }} />
                )}
                {/* <MuiButton
                  variant="outlined"
                  size="small"
                  startIcon={<GetApp />}
                  onClick={handleExportSalaryBonus}
                  style={{ color: "#1C74BC", borderColor: "#1C74BC" }}
                >
                  Export to Excel
                </MuiButton> */}
              </div>

              {/* Tab Content */}
              <div className="salary-bonus-content">
                {activeSalaryBonusTab === "salary" && (
                  <Table
                    title=""
                    aligncss="dashboard-table"
                    header={[
                      { id: "1", label: "SI No" },
                      { id: "2", label: "Employee Name" },
                      { id: "3", label: "Annual CTC" },
                      {
                        id: "4",
                        label: `Base Salary Hike (${formatNumberWithPeriod(
                          config.base_salary_percentage,
                          2,
                        )}%)`,
                      },
                      {
                        id: "5",
                        label: `Variable Salary Hike (${formatNumberWithPeriod(
                          config.variable_salary_percentage,
                          2,
                        )}%)`,
                      },
                      { id: "6", label: "Total Salary Hike" },
                      { id: "7", label: "New CTC" },
                    ]}
                    data={filteredEmployees
                      .filter((emp) => {
                        const search = salaryBonusSearch.trim().toLowerCase();
                        if (!search) return true;
                        const name =
                          `${emp.first_name} ${emp.last_name}`.toLowerCase();
                        return name.includes(search);
                      })
                      .map((emp, index) => {
                        // Try to get calculation from API first, fallback to local calculation
                        const apiCalculation = performanceCalculations?.find(
                          (calc) => calc.employee_id === emp.employee_id,
                        );
                        const calcData = apiCalculation
                          ? {
                              baseSalaryHike:
                                parseFloat(apiCalculation.base_salary_hike) ||
                                0,
                              variableSalaryHike:
                                parseFloat(
                                  apiCalculation.variable_salary_hike,
                                ) || 0,
                              totalSalaryHike:
                                parseFloat(apiCalculation.total_salary_hike) ||
                                0,
                              newCTC: parseFloat(apiCalculation.new_ctc) || 0,
                            }
                          : calculateEmployeeData(emp.employee_id);

                        const ctcValue =
                          parseFloat(ctcValues[emp.employee_id] || 0) || 0;
                        // Format CTC with thousands separator but period for decimals
                        const formattedCTC = ctcValue.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        });
                        return {
                          si_no: index + 1,
                          employee_name: `${emp.first_name} ${emp.last_name}`,
                          annual_ctc: formattedCTC,
                          base_salary_hike: formatNumberWithPeriod(
                            calcData.baseSalaryHike,
                            2,
                          ),
                          variable_salary_hike: formatNumberWithPeriod(
                            calcData.variableSalaryHike,
                            2,
                          ),
                          total_salary_hike: formatNumberWithPeriod(
                            calcData.totalSalaryHike,
                            2,
                          ),
                          new_ctc: formatNumberWithPeriod(calcData.newCTC, 2),
                        };
                      })}
                  />
                )}

                {activeSalaryBonusTab === "bonus" && (
                  <Table
                    title=""
                    aligncss="dashboard-table"
                    header={[
                      { id: "1", label: "SI No" },
                      { id: "2", label: "Employee Name" },
                      { id: "3", label: "Annual CTC" },
                      {
                        id: "4",
                        label: `Base Bonus (${formatNumberWithPeriod(
                          config.base_bonus_percentage,
                          2,
                        )}%)`,
                      },
                      {
                        id: "5",
                        label: `Variable Bonus (${formatNumberWithPeriod(
                          config.variable_bonus_percentage,
                          2,
                        )}%)`,
                      },
                      { id: "6", label: "Total Bonus" },
                      { id: "7", label: "Half Year Bonus" },
                    ]}
                    data={filteredEmployees
                      .filter((emp) => {
                        const search = salaryBonusSearch.trim().toLowerCase();
                        if (!search) return true;
                        const name =
                          `${emp.first_name} ${emp.last_name}`.toLowerCase();
                        return name.includes(search);
                      })
                      .map((emp, index) => {
                        // Try to get calculation from API first, fallback to local calculation
                        const apiCalculation = performanceCalculations?.find(
                          (calc) => calc.employee_id === emp.employee_id,
                        );
                        const calcData = apiCalculation
                          ? {
                              baseBonus:
                                parseFloat(apiCalculation.base_bonus) || 0,
                              variableBonus:
                                parseFloat(apiCalculation.variable_bonus) || 0,
                              totalBonus:
                                parseFloat(apiCalculation.total_bonus) || 0,
                              halfYearBonus:
                                parseFloat(apiCalculation.half_year_bonus) || 0,
                            }
                          : calculateEmployeeData(emp.employee_id);

                        const ctcValue =
                          parseFloat(ctcValues[emp.employee_id] || 0) || 0;
                        // Format CTC with thousands separator but period for decimals
                        const formattedCTC = ctcValue.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        });
                        return {
                          si_no: index + 1,
                          employee_name: `${emp.first_name} ${emp.last_name}`,
                          annual_ctc: formattedCTC,
                          base_bonus: formatNumberWithPeriod(
                            calcData.baseBonus,
                            2,
                          ),
                          variable_bonus: formatNumberWithPeriod(
                            calcData.variableBonus,
                            2,
                          ),
                          total_bonus: formatNumberWithPeriod(
                            calcData.totalBonus,
                            2,
                          ),
                          half_year_bonus: formatNumberWithPeriod(
                            calcData.halfYearBonus,
                            2,
                          ),
                        };
                      })}
                  />
                )}
              </div>
            </>
          )}
        </Paper>
      )}
    </div>
  );
};

export default PerformanceIndex;
